import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReportTarget } from '../../../types/api';

type ReportMode = 'costs' | 'revenue' | 'pnl';
type ReportLayout = 'standard' | 'monthly';

type PersistedReportFormState = {
  fiscalYearId: string;
  startDate: string;
  endDate: string;
  reportMode: ReportMode;
  reportLayout: ReportLayout;
  selectedUnits: string[];
  selectionDefaultsVersion?: number;
};

type SelectableUnit = {
  id: string;
  name: string;
  isActive?: boolean;
};

type ReportCostCenter = {
  Id: string;
  Name: string;
  Number?: number;
  IsActive?: boolean;
  Items?: Array<{
    Id: string;
    Name: string;
    ShortName: string;
    IsActive: boolean;
    CreatedUtc: string;
    CostCenterId: string;
  }>;
};

const REPORT_MODE_STORAGE_KEY = 'report-mode-preference';
const REPORT_LAYOUT_STORAGE_KEY = 'report-layout-preference';
const REPORT_SELECTION_DEFAULTS_VERSION = 1;

const resolveReportScopeKey = (currentTarget: ReportTarget | null, currentProfileKey: string) => {
  return currentTarget ? `${currentTarget.type}:${currentTarget.id}` : (currentProfileKey || 'default-target');
};

const resolveReportFormStateStorageKey = (reportScopeKey: string) => `report-form-state:${reportScopeKey}`;

const readReportModePreference = (): ReportMode => {
  const savedMode = localStorage.getItem(REPORT_MODE_STORAGE_KEY);
  if (savedMode === 'costs' || savedMode === 'revenue' || savedMode === 'pnl') {
    return savedMode;
  }

  return 'costs';
};

const readReportLayoutPreference = (): ReportLayout => {
  const savedLayout = localStorage.getItem(REPORT_LAYOUT_STORAGE_KEY);
  if (savedLayout === 'standard' || savedLayout === 'monthly') {
    return savedLayout;
  }

  return 'standard';
};

const readPersistedReportFormState = (storageKey: string): Partial<PersistedReportFormState> | null => {
  const savedState = localStorage.getItem(storageKey);
  if (!savedState) {
    return null;
  }

  try {
    return JSON.parse(savedState) as Partial<PersistedReportFormState>;
  } catch {
    return null;
  }
};

const normalizeUnitName = (value: string): string =>
  (value || '').trim().toLowerCase().replace(/\s+/g, ' ');

const buildSelectableUnits = (costCenters: ReportCostCenter[]) => {
  if (!costCenters || costCenters.length === 0) {
    return [] as SelectableUnit[];
  }

  const prioritizedUnitName = normalizeUnitName('Lönekostnad Flex');
  const units: SelectableUnit[] = [];

  for (const center of costCenters) {
    if (center.Items && center.Items.length > 0) {
      for (const item of center.Items) {
        if (item.IsActive === false) {
          continue;
        }

        units.push({
          id: item.Id,
          name: item.Name,
          isActive: item.IsActive,
        });
      }
      continue;
    }

    if (center.IsActive === false) {
      continue;
    }

    units.push({
      id: center.Id,
      name: center.Name,
      isActive: center.IsActive,
    });
  }

  return units.sort((a, b) => {
    const aIsPrioritized = normalizeUnitName(a.name) === prioritizedUnitName;
    const bIsPrioritized = normalizeUnitName(b.name) === prioritizedUnitName;

    if (aIsPrioritized && !bIsPrioritized) {
      return -1;
    }
    if (!aIsPrioritized && bIsPrioritized) {
      return 1;
    }

    return a.name.localeCompare(b.name, 'sv');
  });
};

const createDefaultSelectedUnits = (selectableUnits: SelectableUnit[]) => {
  const excluded = normalizeUnitName('Lönekostnad Flex');
  const initial = new Set<string>();

  for (const unit of selectableUnits) {
    const isActive = unit.isActive !== false;
    if (!isActive) {
      continue;
    }
    if (normalizeUnitName(unit.name) === excluded) {
      continue;
    }
    initial.add(unit.id);
  }

  if (initial.size === 0) {
    for (const unit of selectableUnits) {
      if (unit.isActive !== false) {
        initial.add(unit.id);
      }
    }
  }

  return initial;
};

type UseConfigureReportRequestOptions = {
  costCenters: ReportCostCenter[];
  currentProfileKey: string;
  currentTarget: ReportTarget | null;
};

const toLocalDateInput = (date: Date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const validateDateRange = (nextStartDate: string, nextEndDate: string) => {
  if (!nextStartDate || !nextEndDate) {
    return null;
  }

  if (new Date(nextStartDate) > new Date(nextEndDate)) {
    return 'Slutdatum kan inte vara före startdatum.';
  }

  return null;
};

export const useConfigureReportRequest = ({ costCenters, currentProfileKey, currentTarget }: UseConfigureReportRequestOptions) => {
  const isGroupTarget = currentTarget?.type === 'group';
  const reportScopeKey = useMemo(
    () => resolveReportScopeKey(currentTarget, currentProfileKey),
    [currentProfileKey, currentTarget]
  );
  const formStateStorageKey = useMemo(
    () => resolveReportFormStateStorageKey(reportScopeKey),
    [reportScopeKey]
  );
  const hasHydratedFormStateRef = useRef(false);
  const hasInitializedDefaultUnits = useRef(false);
  const shouldMigrateDefaultGroupSelectionRef = useRef(false);

  const {
    currentMonthStartLocal,
    defaultFiscalYearId,
    previousMonthEndLocal,
    previousMonthStartLocal,
    todayLocal,
    yearToDateStartLocal,
  } = useMemo(() => {
    const now = new Date();
    const previousMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const previousMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
    const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const yearToDateStart = new Date(now.getFullYear(), 0, 1);

    return {
      todayLocal: toLocalDateInput(now),
      previousMonthStartLocal: toLocalDateInput(previousMonthStart),
      previousMonthEndLocal: toLocalDateInput(previousMonthEnd),
      currentMonthStartLocal: toLocalDateInput(currentMonthStart),
      yearToDateStartLocal: toLocalDateInput(yearToDateStart),
      defaultFiscalYearId: `${previousMonthStart.getFullYear()}-01`,
    };
  }, []);

  const getDefaultFormState = useCallback((): PersistedReportFormState => ({
    fiscalYearId: defaultFiscalYearId,
    startDate: previousMonthStartLocal,
    endDate: previousMonthEndLocal,
    reportMode: readReportModePreference(),
    reportLayout: readReportLayoutPreference(),
    selectedUnits: [],
  }), [defaultFiscalYearId, previousMonthEndLocal, previousMonthStartLocal]);

  const [fiscalYearId, setFiscalYearId] = useState(defaultFiscalYearId);
  const [startDate, setStartDate] = useState(previousMonthStartLocal);
  const [endDate, setEndDate] = useState(previousMonthEndLocal);
  const [reportMode, setReportMode] = useState<ReportMode>(() => readReportModePreference());
  const [reportLayout, setReportLayout] = useState<ReportLayout>(() => readReportLayoutPreference());
  const [selectedUnits, setSelectedUnits] = useState<Set<string>>(new Set());
  const [dateError, setDateError] = useState<string | null>(null);

  useEffect(() => {
    localStorage.setItem(REPORT_MODE_STORAGE_KEY, reportMode);
  }, [reportMode]);

  useEffect(() => {
    localStorage.setItem(REPORT_LAYOUT_STORAGE_KEY, reportLayout);
  }, [reportLayout]);

  useEffect(() => {
    hasHydratedFormStateRef.current = false;

    const fallbackState = getDefaultFormState();
    let nextState = fallbackState;
    let hasSavedSelectedUnits = false;

    const parsed = readPersistedReportFormState(formStateStorageKey);
    if (parsed) {
      nextState = {
        fiscalYearId: typeof parsed.fiscalYearId === 'string' && parsed.fiscalYearId ? parsed.fiscalYearId : fallbackState.fiscalYearId,
        startDate: typeof parsed.startDate === 'string' && parsed.startDate ? parsed.startDate : fallbackState.startDate,
        endDate: typeof parsed.endDate === 'string' && parsed.endDate ? parsed.endDate : fallbackState.endDate,
        reportMode: parsed.reportMode === 'costs' || parsed.reportMode === 'revenue' || parsed.reportMode === 'pnl'
          ? parsed.reportMode
          : fallbackState.reportMode,
        reportLayout: parsed.reportLayout === 'monthly' || parsed.reportLayout === 'standard'
          ? parsed.reportLayout
          : fallbackState.reportLayout,
        selectedUnits: Array.isArray(parsed.selectedUnits)
          ? parsed.selectedUnits.filter((value): value is string => typeof value === 'string')
          : fallbackState.selectedUnits,
      };
      hasSavedSelectedUnits = Array.isArray(parsed.selectedUnits) && parsed.selectedUnits.some((value) => typeof value === 'string' && value.trim() !== '');
    }

    setFiscalYearId(nextState.fiscalYearId);
    setStartDate(nextState.startDate);
    setEndDate(nextState.endDate);
    setReportMode(nextState.reportMode);
    setReportLayout(nextState.reportLayout);
    setSelectedUnits(new Set(nextState.selectedUnits));
    setDateError(validateDateRange(nextState.startDate, nextState.endDate));

    shouldMigrateDefaultGroupSelectionRef.current = isGroupTarget
      && hasSavedSelectedUnits
      && parsed?.selectionDefaultsVersion !== REPORT_SELECTION_DEFAULTS_VERSION;
    hasInitializedDefaultUnits.current = hasSavedSelectedUnits;
    hasHydratedFormStateRef.current = true;
  }, [formStateStorageKey, getDefaultFormState, isGroupTarget]);

  const selectableUnits = useMemo(() => buildSelectableUnits(costCenters), [costCenters]);
  const allSelectableIds = useMemo(() => selectableUnits.map((unit) => unit.id), [selectableUnits]);
  const isAllSelected = allSelectableIds.length > 0 && allSelectableIds.every((id) => selectedUnits.has(id));

  useEffect(() => {
    if (!hasHydratedFormStateRef.current || allSelectableIds.length === 0) {
      return;
    }

    setSelectedUnits((prev) => {
      const allowedIds = new Set(allSelectableIds);
      const filtered = new Set(Array.from(prev).filter((id) => allowedIds.has(id)));
      if (filtered.size === prev.size) {
        return prev;
      }
      return filtered;
    });
  }, [allSelectableIds]);

  useEffect(() => {
    if (hasInitializedDefaultUnits.current || selectableUnits.length === 0) {
      return;
    }

    setSelectedUnits(createDefaultSelectedUnits(selectableUnits));
    hasInitializedDefaultUnits.current = true;
  }, [selectableUnits]);

  useEffect(() => {
    if (!shouldMigrateDefaultGroupSelectionRef.current || selectableUnits.length === 0) {
      return;
    }

    const excludedUnitIds = new Set(
      selectableUnits
        .filter((unit) => normalizeUnitName(unit.name) === normalizeUnitName('Lönekostnad Flex'))
        .map((unit) => unit.id)
    );

    setSelectedUnits((prev) => new Set(Array.from(prev).filter((unitId) => !excludedUnitIds.has(unitId))));
    shouldMigrateDefaultGroupSelectionRef.current = false;
  }, [selectableUnits]);

  useEffect(() => {
    if (!hasHydratedFormStateRef.current) {
      return;
    }

    localStorage.setItem(formStateStorageKey, JSON.stringify({
      fiscalYearId,
      startDate,
      endDate,
      reportMode,
      reportLayout,
      selectedUnits: Array.from(selectedUnits),
      selectionDefaultsVersion: REPORT_SELECTION_DEFAULTS_VERSION,
    }));
  }, [endDate, fiscalYearId, formStateStorageKey, reportLayout, reportMode, selectedUnits, startDate]);

  const handleStartDateChange = useCallback((value: string) => {
    setStartDate(value);
    setDateError(validateDateRange(value, endDate));
  }, [endDate]);

  const handleEndDateChange = useCallback((value: string) => {
    setEndDate(value);
    setDateError(validateDateRange(startDate, value));
  }, [startDate]);

  const handleFiscalYearChange = useCallback((value: string) => {
    setFiscalYearId(`${value}-01`);
  }, []);

  const applyDateRange = useCallback((nextStartDate: string, nextEndDate: string) => {
    setStartDate(nextStartDate);
    setEndDate(nextEndDate);
    setFiscalYearId(`${new Date(nextStartDate).getFullYear()}-01`);
    setDateError(validateDateRange(nextStartDate, nextEndDate));
  }, []);

  const isPresetActive = useCallback((presetStartDate: string, presetEndDate: string) => {
    return startDate === presetStartDate && endDate === presetEndDate;
  }, [endDate, startDate]);

  const handleReportModeChange = useCallback((
    _event: React.MouseEvent<HTMLElement>,
    nextMode: ReportMode | null
  ) => {
    if (!nextMode) {
      return;
    }

    setReportMode(nextMode);
  }, []);

  const handleReportLayoutChange = useCallback((
    _event: React.MouseEvent<HTMLElement>,
    nextLayout: ReportLayout | null
  ) => {
    if (!nextLayout) {
      return;
    }

    setReportLayout(nextLayout);
  }, []);

  const handleToggleAllUnits = useCallback(() => {
    if (isAllSelected) {
      setSelectedUnits(new Set());
      return;
    }

    setSelectedUnits(new Set(allSelectableIds));
  }, [allSelectableIds, isAllSelected]);

  const handleUnitToggle = useCallback((unitId: string) => {
    setSelectedUnits((prev) => {
      const next = new Set(prev);
      if (next.has(unitId)) {
        next.delete(unitId);
      } else {
        next.add(unitId);
      }
      return next;
    });
  }, []);

  return {
    reportScopeKey,
    currentMonthStartLocal,
    previousMonthEndLocal,
    previousMonthStartLocal,
    todayLocal,
    yearToDateStartLocal,
    fiscalYearId,
    startDate,
    endDate,
    reportMode,
    reportLayout,
    selectedUnits,
    dateError,
    selectableUnits,
    isAllSelected,
    handleStartDateChange,
    handleEndDateChange,
    handleFiscalYearChange,
    applyDateRange,
    isPresetActive,
    handleReportModeChange,
    handleReportLayoutChange,
    handleToggleAllUnits,
    handleUnitToggle,
  };
};
